import API from 'api'
import { getFileData, ISignedUrl } from 'utils/helper'
import { CustomFile } from 'types'
import CommonApi from 'api/common'
import { UserFileTag } from '__generated__/api-types-and-hooks'

interface IUploadFilesProps {
  files: File[]
  tenantId: string
  clientId: string
  userId: string
  fileTag?: UserFileTag
  onProgress?: (fileIndex: number, percentage: number) => void
  abortControllers?: AbortController[]
  onError?: (fileIndex: number, error: string) => void
}

export const uploadFiles = async ({
  files,
  tenantId,
  clientId,
  userId,
  fileTag,
  onProgress,
  abortControllers,
  onError,
}: IUploadFilesProps) => {
  // 1. Get signed URLs for all files
  const signedUrlPromises = files.map((file) => CommonApi.getSignedUrl(file.type, file.name))
  const signedUrlResponses = await Promise.all(signedUrlPromises)
  const signedUrls = signedUrlResponses.map(
    (res) => JSON.parse(res.getSignedUrl.data) as ISignedUrl
  )

  // 2. Upload files to S3 with progress tracking and cancellation support
  const uploadPromises = files.map((file, index) =>
    API.uploadFile(
      signedUrls[index].signedUrl,
      file,
      file.type,
      (percentage) => onProgress?.(index, percentage),
      abortControllers?.[index]?.signal
    ).catch((error) => {
      onError?.(index, error.message)
      return null
    })
  )
  const uploadResults = await Promise.all(uploadPromises)

  // Filter out failed uploads (null values)
  const successfulUploads = uploadResults
    .map((result, index) => ({ result, index }))
    .filter(({ result }) => result !== null)

  // If no successful uploads, return empty array
  if (successfulUploads.length === 0) {
    return []
  }

  // 3. Process file data only for successful uploads
  const successfulFiles = successfulUploads.map(({ index }) => files[index])
  const successfulResults = successfulUploads.map(({ result }) => result as string)
  const successfulSignedUrls = successfulUploads.map(({ index }) => signedUrls[index])

  const customFiles = successfulFiles.map((file) => ({
    ...file,
    name: file.name,
    type: file.type,
    size: file.size,
  })) as CustomFile[]

  const { fileData } = getFileData({
    data: successfulResults,
    files: customFiles,
    signedUrl: successfulSignedUrls,
    tenantId,
    userId: clientId ?? userId,
    isMultiple: true,
    fileTag,
  })
  return Array.isArray(fileData) ? fileData : [fileData]
}
